// Single source of truth for every piece of content on the site.
// Edit here — the UI is generated from it.

import bentex from "../assets/logos/bentex.jpg";
import fmhomeloans from "../assets/logos/fmhomeloans.png";
import livecare from "../assets/logos/livecare.jpg";
import jcomputers from "../assets/logos/jcomputers.jpg";
import scc from "../assets/logos/scc.jpg";
import nycct from "../assets/logos/nycct.jpeg";
import nycctAlt from "../assets/logos/nycct-alt.png";
import ohrhatorah from "../assets/logos/ohrhatorah.jpg";

import blindsight from "../assets/projects/blindsight.jpg";
import raspberry from "../assets/projects/raspberry.jpg";
import pdfdwg from "../assets/projects/pdfdwg.png";
import bartender from "../assets/projects/bartender.png";
import familyfeud from "../assets/projects/familyfeud.jpg";
import battleofbands from "../assets/projects/battleofbands.jpg";
import adblock from "../assets/projects/adblock.jpg";
import chatbot from "../assets/projects/chatbot.jpg";
import bc from "../assets/projects/bc.jpg";
import gamelogo from "../assets/projects/gamelogo.jpg";
import car from "../assets/projects/car.jpg";
import watermark from "../assets/projects/watermark.jpg";

export const profile = {
  name: "Ouri Alkada",
  handle: "ourialkada",
  role: "Senior Software Developer",
  location: "Brooklyn, New York",
  email: "ourialkada@gmail.com",
  resume: "/resume.pdf",
  roles: [
    "Senior Software Developer",
    "Backend & Cloud Engineer",
    "iOS / Android Developer",
    "Database Architect",
  ],
  current: "Bentex",
  tagline:
    "I build the systems companies run on — APIs, data pipelines, cloud automation and the apps on top of them.",
  bio: [
    "I'm a software developer with close to a decade of shipping production systems across web, mobile, cloud and data. Most of my work lives where the business logic is heaviest: .NET and C# services, SQL Server schemas measured in hundreds of objects, Azure Functions and Logic Apps wiring third-party systems together, and the React front ends that make all of it usable.",
    "I hold a B.S. in Computer Information Systems from CUNY New York City College of Technology, where I graduated with a 3.8 GPA as a CUNY Emerging Scholar. Before the backend took over, I spent years building native iOS and Android apps — Swift, Kotlin, React Native, Bluetooth health devices, Twilio voice and video.",
    "I like problems with a lot of moving parts: integrations that have to not break, scheduled jobs that have to run unattended, marketplace feeds that have to reconcile. I care about the details that keep a system boring in production.",
  ],
  socials: [
    { label: "GitHub", url: "https://github.com/ourialkada", icon: "github" },
    {
      label: "LinkedIn",
      url: "https://www.linkedin.com/in/ouri-alkada-46267a109/",
      icon: "linkedin",
    },
    {
      label: "Instagram",
      url: "https://www.instagram.com/ourialkada/",
      icon: "instagram",
    },
    { label: "Email", url: "mailto:ourialkada@gmail.com", icon: "mail" },
  ],
};

export const stats = [
  { value: 10, suffix: "+", label: "Years shipping software" },
  { value: 5, suffix: "", label: "Companies shipped for" },
  { value: 40, suffix: "+", label: "Technologies in production" },
  { value: 3.8, suffix: "", label: "GPA, B.S. Computer Info Systems", decimals: 1 },
];

export const marquee = [
  "C#", ".NET Core", "Azure", "SQL Server", "Entity Framework", "React",
  "TypeScript", "Node", "Java", "Spring Boot", "Swift", "Kotlin",
  "React Native", "MongoDB", "Firebase", "AWS", "Python", "Docker",
  "Kubernetes", "PostgreSQL", "Redis", "Argo CD",
];

export const experience = [
  {
    id: "bentex",
    company: "Bentex",
    role: "Senior Software Developer",
    period: "Mar 2022 — Present",
    start: 2022,
    current: true,
    logo: bentex,
    summary:
      "Own the internal platform that runs inventory, orders and marketplace listings across the company's divisions.",
    stack: ["C#", "Razor", "SQL Server", "Entity Framework", "IIS", "VB.NET"],
    highlights: [
      "Built a job scheduler that runs per-job processes on user-defined schedules, configured from a dashboard UI.",
      "Maintain a large Razor/C# web application tracking inventory, orders and listings end to end.",
      "Designed hundreds of relational tables, views, indexes and stored procedures in SQL Server.",
      "Built the publishing pipeline that tracks, updates and pushes listings to Amazon, Target, Walmart and other marketplaces.",
      "Use Entity Framework across multiple databases for reads and writes throughout the platform.",
      "Automated nightly database backups, scheduled emailers and reporting jobs via scripts.",
      "Stood up and hardened servers running SQL Server and IIS, accessed over Remote Desktop.",
      "Delivered a VB + stored-procedure Windows Forms app used by three divisions to track factory changes, orders and management.",
    ],
  },
  {
    id: "fm",
    company: "FM Home Loans",
    role: "Senior Software Developer",
    period: "Mar 2020 — Mar 2022",
    start: 2020,
    logo: fmhomeloans,
    summary:
      "Built the cloud backbone and loan-origination front end for a mortgage lender on Azure.",
    stack: [".NET Core", "C#", "Azure Functions", "Logic Apps", "React", "Redux", "Auth0"],
    highlights: [
      "Authored dozens of Azure Functions and Logic Apps for time-based operations and third-party webhooks, using cloud queues and event triggers.",
      "Developed C# / .NET Core Web APIs following S.O.L.I.D. design principles.",
      "Designed a document-management service that merges hundreds of documents with loan data daily and drives an e-signature workflow.",
      "Built an internal React app with Azure AD auth, integrating OneDrive, Outlook and other services through the Microsoft Graph API.",
      "Developed SQL schemas — stored procedures, functions, triggers and views — with Entity Framework on .NET Core.",
      "Shipped a loan-application platform in React, Redux and Router with CI/CD to Azure blob storage, Azure Functions on the backend and Azure Front Door as the proxy layer.",
      "Integrated Auth0 as the identity provider with JWT on client and server, including custom rules, templates, login pages and an Azure AD enterprise connection.",
    ],
  },
  {
    id: "livecare",
    company: "LiveCare Corp.",
    role: "iOS / Android Developer",
    period: "Feb 2019 — Dec 2019",
    start: 2019,
    logo: livecare,
    summary:
      "Shipped connected-health mobile apps talking to Bluetooth monitors, Twilio and Firebase.",
    stack: ["Swift", "Objective-C", "Android", "React Native", "Flutter", "Firebase", "Twilio"],
    highlights: [
      "Created, designed and released an iOS application from scratch.",
      "Debugged, refactored, redesigned and released the Android application.",
      "Used the Twilio API for voice calls, video calls and SMS.",
      "Integrated Firebase Auth, Hosting, Cloud Functions and FCM across projects.",
      "Wrote Node and Java Spring Boot services on AWS for backend HTTP requests.",
      "Implemented Bluetooth on Android to read, write and scan data from health monitors.",
      "Built apps with React Native and Flutter alongside the native codebases.",
      "Migrated an iOS codebase from Objective-C to Swift.",
    ],
  },
  {
    id: "jcomputers",
    company: "JComputers",
    role: "IT Professional",
    period: "Jun 2018 — Nov 2018",
    start: 2018,
    logo: jcomputers,
    summary:
      "Kept hardware, networks and end users running — support, infrastructure and security.",
    stack: ["Windows Server", "Networking", "Backups", "Security"],
    highlights: [
      "Provided technical support in person, by phone and over remote access.",
      "Maintained and troubleshot servers, workstations, printers and mobile devices.",
      "Installed and configured new hardware and software for compatibility and reliability.",
      "Wrote IT policies and procedures for effective use of technology resources.",
      "Managed network infrastructure: routers, switches, firewalls and wireless access points.",
      "Ran regular backups and disaster-recovery testing to guarantee availability.",
      "Monitored system performance and resolved issues before they reached end users.",
      "Conducted security audits and implemented protections for company data and systems.",
    ],
  },
  {
    id: "scc",
    company: "Sephardic Community Center",
    role: "Web / Mobile Developer",
    period: "Sep 2015 — Jun 2018",
    start: 2015,
    logo: scc,
    summary:
      "First engineering role — built the center's website plus its iOS and Android staff apps.",
    stack: ["Java", "Swift", "Android", "Spring Boot", "MySQL", "C++", "Git"],
    highlights: [
      "Designed and developed a user-friendly website, including an optimized checkout that improved program enrollment.",
      "Built an Android app in Java for staff to manage ongoing programs at the facility.",
      "Built the matching iOS app in Xcode and Swift, using Cocoa, CocoaTouch, Alamofire and UIKit.",
      "Wrote C++ programs for tracking records, storing and retrieving data and controlling equipment.",
      "Implemented MySQL persistence across the mobile apps.",
      "Built backend APIs with Java Spring Boot.",
      "Shipped frequent UI and functionality updates to both the website and the apps, versioned in Git.",
    ],
  },
];

export const skills = [
  {
    group: "Languages",
    items: ["C#", "VB / VBA .NET", "Java", "Swift", "Kotlin", "JavaScript", "TypeScript", "Python", "C / C++", "SQL"],
  },
  {
    group: "Web",
    items: ["React", "Redux", "React Router", "HTML5", "CSS3", "Razor", "JSP", "REST APIs"],
  },
  {
    group: "Mobile",
    items: ["Swift / UIKit", "Android SDK", "React Native", "Flutter", "Xamarin", "Bluetooth LE"],
  },
  {
    group: "Backend",
    items: [".NET Core", "Entity Framework", "Spring Boot", "Node.js", "Azure Functions", "Logic Apps", "Auth0", "Twilio"],
  },
  {
    group: "Data",
    items: ["SQL Server", "PostgreSQL", "MySQL", "MongoDB", "Redis", "Firebase / Firestore", "Stored Procedures", "Indexing & Views"],
  },
  {
    group: "Cloud & Ops",
    items: ["Azure", "AWS", "Kubernetes", "k3s", "Argo CD", "GitOps", "Docker", "Firebase", "IIS", "CI/CD", "Nginx", "Windows Server"],
  },
  {
    group: "Tooling",
    items: ["Git", "GitHub", "Azure DevOps", "BitBucket", "Visual Studio", "VS Code", "Xcode", "Jira", "Trello"],
  },
];

export const projects = [
  {
    id: "watermark",
    title: "Watermark Adder",
    blurb:
      "A server-side tool that watermarks thousands of AutoCAD drawings automatically — drop a directory in, get an email with the processed files back.",
    tags: ["VBA", "AutoCAD API", "C#"],
    image: watermark,
    href: "/projects/watermark",
    repo: "https://github.com/ourialkada/WatermarkAdder",
  },
  {
    id: "pi-k8s",
    title: "Raspberry Pi Kubernetes Cluster",
    blurb:
      "A mini rack of Raspberry Pis running a k3s cluster. Postgres and Redis run on it as real stateful workloads, and Argo CD keeps the whole thing in sync with Git — push a manifest and the cluster reconciles itself.",
    tags: ["Kubernetes", "k3s", "Argo CD", "Postgres", "Redis"],
    image: raspberry,
  },
  {
    id: "pdf-dwg",
    title: "PDF to DWG Converter",
    blurb:
      "Turns PDF floor plans and drawings back into editable DWG geometry, rather than the flat image you get from pasting a PDF into CAD.",
    tags: ["AutoCAD API", "C#", "CAD"],
    image: pdfdwg,
  },
  {
    id: "pi-server",
    title: "Raspberry Pi NAS",
    blurb:
      "A Raspberry Pi running Raspbian turned into a network file server — extra storage reachable from any machine on the LAN.",
    tags: ["Hardware", "Linux", "Networking"],
    image: blindsight,
  },
  {
    id: "pi-adblock",
    title: "Network Ad Blocker",
    blurb:
      "A Raspberry Pi wired into the router that filters ads and trackers for every device on the network, DNS-level.",
    tags: ["Hardware", "Linux", "DNS"],
    image: adblock,
  },
  {
    id: "atom",
    title: "Atom Chatbot",
    blurb:
      "A conversational assistant for CUNY NYCCT, built years before LLMs made this easy. New students could ask where the Bursar is, which room a professor's office is in, or when their office hours are, and get a plain-language answer back — all of it hand-built intent matching, no model to call.",
    tags: ["NLP", "Python", "Education"],
    image: chatbot,
  },
  {
    id: "bc-ai",
    title: "Breast Cancer Prediction",
    badge: "Published",
    blurb:
      "A classifier that predicts whether a tumour is benign or malignant from patient diagnostic data, built in Python with scikit-learn. Published in the Journal of Computing Sciences in Colleges, co-authored with Professor Marcos Pinto and Hsinrong Wei.",
    tags: ["Machine Learning", "scikit-learn", "Python", "Research"],
    image: bc,
    href: "https://dl.acm.org/doi/abs/10.5555/3282588.3282598",
  },
  {
    id: "atomroid",
    title: "Atomroid",
    blurb:
      "An arcade game with three modes — Colorroid (shoot asteroids by color), AtomDodge (dodge incoming fire) and One in the Chamber (one bullet per asteroid).",
    tags: ["Game", "Unity", "Mobile"],
    image: gamelogo,
  },
  {
    id: "bartender",
    title: "Automated Bartender",
    blurb:
      "A drink machine modelled from scratch in CAD — enclosure, pump and tubing routing, and a touch screen on the front to pick a drink and pour it.",
    tags: ["CAD", "Hardware", "Embedded"],
    image: bartender,
  },
  {
    id: "familyfeud",
    title: "Family Feud",
    blurb:
      "A playable Family Feud clone — the board reveal, the strikes and the round scoring, driven from a host screen.",
    tags: ["Game", "JavaScript"],
    image: familyfeud,
  },
  {
    id: "battleofbands",
    title: "Battle of the Bands",
    // Placeholder — no description of this one survived anywhere in the repo.
    blurb: "A site built for a Battle of the Bands event.",
    tags: ["Web", "Events"],
    image: battleofbands,
  },
  {
    id: "arduino-car",
    title: "Arduino Car & Skateboard",
    blurb:
      "A 3D-printed toy car driven by Arduino-controlled motors — then the same motor control ported onto a motorized skateboard.",
    tags: ["Arduino", "3D Printing", "Embedded"],
    image: car,
  },
];

export const education = [
  {
    id: "bs",
    school: "CUNY New York City College of Technology",
    degree: "B.S. — Computer Information Systems",
    period: "2017 — 2019",
    gpa: "3.8 GPA",
    logo: nycct,
    honors: [
      "CUNY Emerging Scholars",
      "National Society of Collegiate Scholars",
      "Dean's List 2017–2019",
    ],
    coursework: [
      "Discrete Structures",
      "Computer Architecture",
      "Design & Analysis of Algorithms",
      "Databases",
      "Computer Networking",
      "Machine Learning",
      "Backend Development (Spring Boot)",
      "Mobile Development (React Native)",
    ],
  },
  {
    id: "aas",
    school: "CUNY New York City College of Technology",
    degree: "A.A.S. — Computer Systems Technology",
    period: "2015 — 2017",
    gpa: "3.9 GPA",
    logo: nycctAlt,
    honors: ["Dean's List", "Hillel Leadership Award", "Emerging Scholars Award"],
    coursework: [
      "Java Programming",
      "Web Programming",
      "C# Programming",
      "Mobile Development (Swift & Java)",
      "C++ Programming",
      "Object Oriented Programming",
      "Data Structures",
      "Calculus & Differential Equations",
      "Linear Algebra",
      "Discrete Algorithms I & II",
    ],
  },
  {
    id: "hs",
    school: "Ohr Hatorah High School",
    degree: "Advanced Regents Diploma — Computer Science",
    period: "2011 — 2015",
    gpa: "3.81 GPA",
    logo: ohrhatorah,
    honors: ["School Service Award", "Advanced Regents Diploma"],
    coursework: [
      "Introductory Programming",
      "Calculus I (University at Albany)",
      "Micro-Economics (Touro College)",
      "Physics & Chemistry",
      "Global & U.S. History",
    ],
  },
];

export const awards = [
  {
    title: "Health care AI: predicting breast cancer with machine learning",
    org: "Journal of Computing Sciences in Colleges, vol. 34 no. 2, pp. 65–71 — with M. Pinto and H. Wei",
    year: "Dec 2018",
    url: "https://dl.acm.org/doi/abs/10.5555/3282588.3282598",
  },
  {
    title: "CUNY Emerging Scholars Award",
    org: "CUNY New York City College of Technology",
    year: "2015 — 2019",
  },
  {
    title: "National Society of Collegiate Scholars",
    org: "CUNY New York City College of Technology",
    year: "2017 — 2019",
  },
  {
    title: "Hillel Leadership Award",
    org: "CUNY New York City College of Technology",
    year: "2015 — 2017",
  },
  {
    title: "Dean's List",
    org: "CUNY New York City College of Technology",
    year: "2015 — 2019",
  },
  { title: "School Service Award", org: "Ohr Hatorah High School", year: "2011 — 2015" },
  { title: "Advanced Regents Diploma", org: "Ohr Hatorah High School", year: "2015" },
];

export const contact = {
  // Formspree endpoint used by the contact form.
  endpoint: "https://formspree.io/f/xpzyvgnd",
};
